import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Download } from "lucide-react";

import messages from "../../../../assets/locale/messages";
import logo from "../../../../assets/images/yoram-logo-export-white.svg";
import { fetchImageAsBase64 } from "../../../../utils/Helpers";
import { isSuperAdmin } from "../../../../utils/Permissions";
import { UIActionModal, UIButton } from "../../../../components/ui";
import { updateApplicant } from "../../../../store/applicants/actions";
import { getCompanyApplicantProfileRequest } from "../../../../store/CompanyPortal/ApplicantProfile/actions";
import { downloadProfile } from "../../../Admin-portal/Applicants/downloadProfile";
import { getApplicantProfileRoutes } from "./profileHelpers";

function ApplicantProfileHeaderActions({
  applicantId,
  portalVariant,
  profile,
  showEditButton,
}) {
  const dispatch = useDispatch();
  const history = useHistory();
  const lang = useSelector((state) => state.locale.lang);
  const { applicants, shared } = messages[lang];
  const isAdminPortal = portalVariant === "admin-portal";
  const [openActionModal, setOpenActionModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [userBase, setUserBase] = useState(null);
  const [proLineLogo, setProLineLogo] = useState(null);

  const profileRoutes = getApplicantProfileRoutes(portalVariant, applicantId);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const [profileImage, brandLogo] = await Promise.all([
        profile?.image ? fetchImageAsBase64(profile.image) : Promise.resolve(null),
        fetchImageAsBase64(logo),
      ]);
      if (!cancelled) {
        setUserBase(profileImage);
        setProLineLogo(brandLogo);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [profile?.image]);

  const handleDownloadProfile = () => {
    if (!profile) return;
    downloadProfile(profile, applicants, lang, proLineLogo, userBase);
  };

  const handleConfirmBlockAction = () => {
    dispatch(
      updateApplicant({
        id: applicantId,
        params: { is_blocked: modalType === "block" },
        onSuccess: () => {
          dispatch(
            getCompanyApplicantProfileRequest({
              id: applicantId,
              portal: "admin",
            }),
          );
        },
      }),
    );
    setOpenActionModal(false);
  };

  const showDownload = true;
  const showEdit = isAdminPortal && showEditButton && isSuperAdmin();
  const showBlock = isAdminPortal && profile?.is_blocked === false;
  const showUnBlock = isAdminPortal && profile?.is_blocked === true;

  if (!showDownload && !showEdit && !showBlock && !showUnBlock) {
    return null;
  }

  return (
    <>
      {showDownload ? (
        <UIButton
          aria-label={applicants.applicantDetails.downloadProfile}
          color="inherit"
          variant="outlined"
          onClick={handleDownloadProfile}
        >
          <Download size={16} />
        </UIButton>
      ) : null}
      {showEdit ? (
        <UIButton
          aria-label={shared.editProfile}
          color="inherit"
          variant="outlined"
          onClick={() => history.push(profileRoutes.edit)}
        >
          {shared.editProfile}
        </UIButton>
      ) : null}
      {showBlock ? (
        <UIButton
          variant="destructive"
          onClick={() => {
            setModalType("block");
            setOpenActionModal(true);
          }}
        >
          {applicants.applicantDetails.blockBtn}
        </UIButton>
      ) : null}
      {showUnBlock ? (
        <UIButton
          variant="success"
          onClick={() => {
            setModalType("unBlock");
            setOpenActionModal(true);
          }}
        >
          {applicants.applicantDetails.unBlockBtn}
        </UIButton>
      ) : null}
      {isAdminPortal ? (
        <UIActionModal
          open={openActionModal}
          title={modalType === "block" ? shared.block : shared.unBlock}
          variant={modalType === "block" ? "reject" : "approve"}
          onClose={() => setOpenActionModal(false)}
          actions={
            <UIActionModal.Actions
              cancelLabel={shared.cancel}
              confirmLabel={
                modalType === "block" ? shared.confirm : shared.unBlock
              }
              onCancel={() => setOpenActionModal(false)}
              onConfirm={handleConfirmBlockAction}
            />
          }
        >
          <p className="ui-action-modal__body-text">
            {modalType === "block"
              ? applicants.applicantDetails.blockModalConfirmMessage
              : applicants.applicantDetails.unBlockModalConfirmMessage}
          </p>
        </UIActionModal>
      ) : null}
    </>
  );
}

ApplicantProfileHeaderActions.propTypes = {
  applicantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  portalVariant: PropTypes.oneOf(["admin-portal", "company-portal"]).isRequired,
  profile: PropTypes.object,
  showEditButton: PropTypes.bool,
};

ApplicantProfileHeaderActions.defaultProps = {
  profile: null,
  showEditButton: true,
};

export default ApplicantProfileHeaderActions;
